import { useEffect, useRef, useCallback } from 'react';

// ── COMING BACK LANDS WHERE YOU LEFT ─────────────────────────────────────────
// Opening a listing is a real navigation (see ProductPage), so pressing Back
// re-mounts the page that linked to it — and scroll position does not survive
// that. A reader who had scrolled down to a product grid and opened something
// was returned to the top, above everything they had already scrolled past.
//
// THE BROWSER CANNOT DO THIS FOR US. Its own restoration measures the page at
// the moment of the navigation, and at that moment the grid is empty: rows
// arrive from Firestore a beat later, so there is nothing yet to scroll to and
// the attempt is silently clamped to the top.
//
// Hence: written on the way out by the link that leaves, replayed once the
// content actually has height, and CONSUMED — so a later ordinary visit to the
// same page starts at the top, where it should.
//
// `ready` is what says the content has height. Pass `!loading`, or anything
// else that is false while the list is still empty.
export default function useRestoreScroll(key, ready) {
  const done = useRef(false);

  useEffect(() => {
    if (!ready || done.current) return;
    done.current = true;
    let y = 0;
    try {
      y = Number(sessionStorage.getItem(key) || 0);
      sessionStorage.removeItem(key);
    } catch { /* private mode — not worth failing a render over */ }
    if (!y) return;
    // After paint, or the grid's own height is not on the page yet.
    requestAnimationFrame(() => window.scrollTo(0, y));
  }, [key, ready]);

  // Hand this to the onClick of whatever navigates away.
  return useCallback(() => {
    try { sessionStorage.setItem(key, String(window.scrollY)); } catch { /* as above */ }
  }, [key]);
}
